import { useEffect, useState, useCallback } from 'react';
import { Switch, Table } from 'antd';
import { Add as AddIcon, ContentCopy as CopyIcon } from '@mui/icons-material';
import { Button, IconButton } from '@mui/material';
import { useSnackbar } from 'notistack';
import EditIcon from '@mui/icons-material/Edit';
import { getCleanseRulesByEntityId, updateActiveCleanseStatus } from "../../../api's/DataModelApi";
import AddRuleModal from '../cleanse-modal/AddRuleModal';
import EditRuleModal from './cleanse-modal/EditRuleModal';
import CopyRuleModal from './cleanse-modal/CopyRuleModal';

const styles = {
  container: {
    padding: '20px',
    backgroundColor: '#f8f9fa',
    minHeight: '500px',
  },
  header: {
    marginBottom: '24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: '20px',
    fontWeight: '500',
    color: '#333',
  },
  statusBadge: {
    display: 'inline-block',
    padding: '6px 16px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '500',
    textAlign: 'center',
    minWidth: '80px',
  },
  statusActive: {
    backgroundColor: '#d4edda',
    color: '#155724',
  },
  statusOff: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
  },
};

const CleanseTab = ({ entityName, entityId, entities, attributes }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openCopyModal, setOpenCopyModal] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 5 });

  const [editRuleData, setEditRuleData] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const fetchCleanseRules = useCallback(async () => {
    if (!entityId) return;
    try {
      setLoading(true);
      const data = await getCleanseRulesByEntityId(entityId);
      const rulesArray = Array.isArray(data) ? data : Array.isArray(data?.rulesDtos) ? data.rulesDtos : [];

      setRules(
        rulesArray.map((rule, index) => ({
          key: rule.cleanse_id ?? index + 1,
          cleanse_id: rule.cleanse_id,
          rule_name: rule.rule_name,
          appliesTo: rule.appliesTo?.join(', ') || '-',
          rule_type: rule.rule_type,
          description: rule.description,
          status: rule.status,
        }))
      );
    } catch (error) {
      console.error('Error fetching cleanse rules:', error);
      enqueueSnackbar('Failed to load cleanse rules', { variant: 'error' });
      setRules([]);
    } finally {
      setLoading(false);
    }
  }, [entityId, enqueueSnackbar]);

  useEffect(() => {
    fetchCleanseRules();
  }, [fetchCleanseRules]);

  const handleRuleAdded = async () => {
    await fetchCleanseRules();
    enqueueSnackbar('Rule added successfully', { variant: 'success' });
  };

  const handleRuleCopied = async () => {
    await fetchCleanseRules();
    enqueueSnackbar('Rule copied successfully', { variant: 'success' });
  };

  const handleActiveStatus = async (id) => {
    setLoading(true);
    try {
      console.log({ id });
      const response = await updateActiveCleanseStatus(id);
      if (response.status === 200) {
        enqueueSnackbar('Status update!', { variant: 'success' });
        await fetchCleanseRules();
      }
    } catch (error) {
      enqueueSnackbar('Failed to update status!', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'rule_name',
      key: 'rule_name',
      sorter: (a, b) => a.rule_name.localeCompare(b.rule_name),
      defaultSortOrder: 'ascend',
      render: (value) => <b>{value}</b>,
    },
    { title: 'Applies To', dataIndex: 'appliesTo', key: 'appliesTo' },
    { title: 'Rule Type', dataIndex: 'rule_type', key: 'rule_type' },
    { title: 'Description', dataIndex: 'description', key: 'description' },
    {
      title: 'Active',
      dataIndex: 'status',
      key: 'status',
      render: (value, record) => (
        <Switch
          checked={!!value}
          checkedChildren="Yes"
          unCheckedChildren="No"
          onChange={() => handleActiveStatus(record.cleanse_id)}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <IconButton
          size="small"
          onClick={() => {
            setEditRuleData(record);
            setOpenEditModal(true);
          }}
          aria-label="edit"
        >
          <EditIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Cleanse Rules for {entityName}</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <Button onClick={() => setOpenAddModal(true)} variant="contained" startIcon={<AddIcon />} size="small">
            Add Rule
          </Button>

          <Button onClick={() => setOpenCopyModal(true)} variant="contained" startIcon={<CopyIcon />} size="small">
            Copy Rule
          </Button>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={rules}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: rules.length,
          onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
          showSizeChanger: true,
          pageSizeOptions: ['5', '10', '20', '50'],
        }}
        bordered
      />

      <AddRuleModal
        entityName={entityName}
        entityId={entityId}
        entities={entities}
        attributes={attributes}
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        onSave={handleRuleAdded}
      />

      <CopyRuleModal
        entityId={entityId}
        attributes={attributes}
        open={openCopyModal}
        onClose={() => setOpenCopyModal(false)}
        onSuccess={handleRuleCopied}
      />
      <EditRuleModal
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        rule={editRuleData}
        attributes={attributes}
        entityId={entityId}
        onUpdate={async () => {
          await fetchCleanseRules();
          enqueueSnackbar('Rule updated successfully', { variant: 'success' });
        }}
      />
    </div>
  );
};

export default CleanseTab;
